import { Injectable } from '@angular/core';
import { Http, RequestOptionsArgs, Response } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import { ResponseWrapper } from './response-wrapper';
import { Reauthenticator } from './reauthenticator';
import { NativeOptionsBuilder } from './native-options-builder';
import { StateService } from '../state/state.service';

export interface EndpointOptions {
  secure: boolean;
  mapper?: (data: any, index?: number) => any;
  resource: boolean;
  myfoglioAuth?: string;
  myfoglioJwt?: string;
}

@Injectable()
export class EndpointService {

  private readonly RETRY_LIMIT = 2;
  private readonly INIT_TIMEOUT = 2500;

  private _reauthenticator: Reauthenticator;

  set reauthenticator(reauthenticator: Reauthenticator) {
    this._reauthenticator = reauthenticator;
  }

  constructor(private http: Http,
    private state: StateService,
    private optionsBuilder: NativeOptionsBuilder) {
  }

  
  resource(path: string, options: EndpointOptions): Observable<ResponseWrapper<any>> {
    options.resource = true;

    return this.request(
      path,
      (realPath, nativeOptions) => this.http.get(realPath, nativeOptions),
      options
    );
  }

  delete<T>(path: string, options: EndpointOptions): Observable<ResponseWrapper<T>> {
    return this.request<T>(
      path,
      (realPath, nativeOptions) => this.http.delete(realPath, nativeOptions),
      options
    );
  }

  post<T>(path: string, data: any, options?: EndpointOptions): Observable<ResponseWrapper<T>> {
    return this.request<T>(
      path,
      (realPath, nativeOptions) => this.http.post(realPath, data, nativeOptions),
      options
    );
  }

  put<T>(path: string, data: any, options: EndpointOptions): Observable<ResponseWrapper<T>> {
    return this.request<T>(
      path,
      (realPath, nativeOptions) => this.http.put(realPath, data, nativeOptions),
      options
    );
  }

  getOne<T>(path: string, options?: EndpointOptions): Observable<ResponseWrapper<T>> {
    return this.request<T>(
      path,
      (realPath, nativeOptions) => this.http.get(realPath, nativeOptions),
      options
    );
  }

  getMany<T>(path: string, options?: EndpointOptions): Observable<ResponseWrapper<T[]>> {
    const inner = options.mapper;
    options.mapper = results => results ? results.map((e, i) => inner(e, i)) : [];

    return this.request<T[]>(
      path,
      (realPath, nativeOptions) => this.http.get(realPath, nativeOptions),
      options
    );
  }

  private request<T>(path: string,
    requester: (path: string, options: RequestOptionsArgs) => Observable<Response>,
    options: EndpointOptions) {
    return this.doRequest(path, requester, options)
      .first()
      .map(response => new ResponseWrapper<T>(response, this.doMap(response, options.mapper, options.resource)));
  }

  private doRequest<T>(path: string,
    requester: (path: string, options: RequestOptionsArgs) => Observable<Response>,
    options: EndpointOptions): Observable<Response> {
    const init$ = options.secure ? this.afterStateInit() : Observable.of(true),
      limit = this.RETRY_LIMIT;
    let nativeOptions = null,
      count = 0;

    return init$
      .do(() => {
        nativeOptions = this.buildNativeOptions(options);
      })
      .switchMap(() => requester(path, nativeOptions))
      .retryWhen(errors => errors
        .switchMap(error => {
          if (count >= limit) {
            return Observable.throw(error);
          }
          if (!this.shouldRetry(error, options)) {
            return Observable.throw(error);
          }
          count++;
          nativeOptions = this.buildNativeOptions(options);

          return this._reauthenticator
            .reauthenticate(error)
            .switchMap(retry => retry ? requester(path, nativeOptions) : Observable.throw(error));
        }));
  }

  private shouldRetry(error: any, options: EndpointOptions) {
    if (!options.secure || !this._reauthenticator || !(error instanceof Response)) {
      return false;
    }

    return error.status === 401 || error.status === 403;
  }

  private buildNativeOptions<T>(options: EndpointOptions): RequestOptionsArgs {

    return this.optionsBuilder.build(options);
  }

  private doMap<T>(response: Response, mapper: (data: any, index?: number) => T, resource: boolean) {
    let raw: any;
    if (resource) {
      raw = response;
    } else {
      try {
        raw = response.json();
      } catch (e) {
        raw = null;
      }
    }

    return (mapper && raw) ?
      mapper(raw) :
      null;
  }

  private afterStateInit(): Observable<any> {
    return Observable.race<any>(this.state.initialized$, Observable.timer(this.INIT_TIMEOUT));
  }
}



// WEBPACK FOOTER //
// ./src/app/core/endpoint.service.ts